import type { DocumentReference } from '@/components/layout/chat-pane.types'

/**
 * Pure HTML/DOM utility functions for ContentEditableChatInput
 * Extracted to reduce main component file size
 */

/** Match @mention query at end of text before cursor (supports folder paths with `/`). */
export const MENTION_QUERY_AT_CURSOR_RE = /(?<!\w)@(\/?[^\s@]*)$/

export function parseMentionQueryAtCursor(textBeforeCursor: string): { full: string; query: string } | null {
  const match = textBeforeCursor.match(MENTION_QUERY_AT_CURSOR_RE)
  if (!match) return null
  return { full: match[0], query: match[1] ?? '' }
}

/** Remove the active @mention trigger from any text node in the editor (used before inserting a chip). */
export function removeMentionTriggerFromEditor(root: HTMLElement): boolean {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null)
  let removed = false
  let node: Node | null
  while ((node = walker.nextNode())) {
    const textNode = node as Text
    const content = textNode.textContent ?? ''
    const match = content.match(MENTION_QUERY_AT_CURSOR_RE)
    if (!match || match.index === undefined) continue
    const start = match.index
    textNode.textContent = content.slice(0, start) + content.slice(start + match[0].length)
    removed = true
  }
  return removed
}

/** @deprecated prefer removeMentionTriggerFromEditor */
export function removeMentionTriggerFromLastTextNode(root: HTMLElement): boolean {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null)
  let lastTextNode: Text | null = null
  let node: Node | null
  while ((node = walker.nextNode())) {
    lastTextNode = node as Text
  }
  if (!lastTextNode) return false
  const content = lastTextNode.textContent ?? ''
  const match = parseMentionQueryAtCursor(content)
  if (!match) return false
  const start = content.length - match.full.length
  lastTextNode.textContent = content.slice(0, start)
  return true
}

/** Strip leading/trailing slashes for folder/document mention search. */
export function normalizeMentionSearchQuery(raw: string): string {
  return raw.trim().replace(/^\/+/, '').replace(/\/+$/, '')
}

/** True when the user is typing a path-style folder mention (@/users or @users/rowan). */
export function isFolderLikeMentionQuery(raw: string): boolean {
  const trimmed = raw.trim()
  return trimmed.startsWith('/') || trimmed.includes('/')
}

const CHIP_REMOVE_BUTTON_CLASS =
  'inline-flex h-3 w-3 shrink-0 items-center justify-center rounded-sm p-0 leading-none opacity-70 hover:opacity-100 hover:bg-black/10 dark:hover:bg-white/10'

/**
 * Chip chrome padding restored (pre-50d799ac). Keep leading-none + single-line HTML
 * markup so composer text-[15px] cannot inflate chip height via whitespace flex items.
 */
const INLINE_CHIP_BASE_CLASS =
  'inline-flex items-center gap-1 pl-1.5 pr-1 py-0.5 mx-1 my-0.5 align-middle rounded-md text-xs font-medium leading-none border transition-all select-none'

export function escapeHtml(text: string): string {
  const div = document.createElement('div')
  div.textContent = text
  return div.innerHTML
}

export function renderSkillChipAsHTML(skill: { id: string; name: string }): string {
  const name = escapeHtml(skill.name)
  const id = escapeHtml(skill.id)
  // Single-line markup: whitespace text nodes inside inline-flex inherit composer line-height and over-pad chips.
  return `<span data-skill-id="${id}" data-skill-name="${name}" data-reference-type="chip" contenteditable="false" class="${INLINE_CHIP_BASE_CLASS} cursor-default bg-violet-500/10 text-violet-600 dark:text-violet-400 border-violet-500/20">⚡<span class="max-w-[150px] truncate leading-none">${name}</span><button data-skill-remove type="button" class="${CHIP_REMOVE_BUTTON_CLASS}" onmousedown="event.preventDefault()"><svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg></button></span>`
}

export function renderChipAsHTML(ref: DocumentReference, isLast: boolean, isPasted?: boolean): string {
  // Use paragraph icon (¶) for pasted references
  const paragraphIcon = '¶'

  const refKind: 'folder' | 'document' =
    ref.kind === 'folder' || ref.type === 'folder' ? 'folder' : 'document'
  const folderPath =
    refKind === 'folder' ? String(ref.path ?? ref.id ?? '').trim() : ''
  const docPathAttr =
    refKind === 'document' ? String(ref.path ?? '').trim() : ''
  
  const iconMap: Record<string, string> = {
    work_spec: '📄',
    note: '📝',
    voice_memo: '🎤',
    meeting: '📅',
    idea: '💡',
    research: '🔬',
    todo: '📋',
    folder: '📁',
  }

  const colorMap: Record<string, string> = {
    work_spec: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
    voice_memo: 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20',
    meeting: 'bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20',
    idea: 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 border-yellow-500/20',
    research: 'bg-pink-500/10 text-pink-600 dark:text-pink-400 border-pink-500/20',
    todo: 'bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20',
    folder: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
  }

  const icon = isPasted ? paragraphIcon : (iconMap[ref.type] || '📄')
  const colorClass = colorMap[ref.type] || 'bg-gray-500/10 text-gray-600 dark:text-gray-400 border-gray-500/20'
  const title = escapeHtml(ref.title)
  
  // Range stays visible: only the title truncates (two chips for one doc must be tellable apart).
  const lineRangeLabel =
    ref.lineStart !== undefined
      ? ref.lineEnd !== undefined && ref.lineEnd !== ref.lineStart
        ? `${ref.lineStart}-${ref.lineEnd}`
        : `${ref.lineStart}`
      : ''
  const rangeHtml = lineRangeLabel
    ? `<span class="shrink-0 leading-none opacity-70" data-reference-range>:${lineRangeLabel}</span>`
    : ''

  const lineDataAttrs =
    ref.lineStart !== undefined
      ? ` data-line-start="${ref.lineStart}" data-line-end="${ref.lineEnd ?? ref.lineStart}"`
      : ''
  const excerptAttr = ref.excerpt
    ? ` data-excerpt="${escapeHtml(clipPasteReferenceExcerpt(ref.excerpt))}"`
    : ''
  const tailText = storedPasteTail(ref.excerpt, ref.excerptTail)
  const tailAttr = tailText ? ` data-excerpt-tail="${escapeHtml(tailText)}"` : ''

  const pathAttr =
    refKind === 'folder'
      ? escapeHtml(folderPath || ref.id)
      : escapeHtml(docPathAttr)

  // Single-line markup: avoid whitespace flex items that inherit composer line-height.
  return `<span data-reference-id="${escapeHtml(ref.id)}" data-reference-type="chip" data-reference-kind="${refKind}" data-reference-path="${pathAttr}" data-reference-title="${title}" contenteditable="false"${lineDataAttrs}${excerptAttr}${tailAttr} class="${INLINE_CHIP_BASE_CLASS} cursor-pointer hover:scale-105 hover:shadow-sm ${colorClass}">${icon}<span class="max-w-[150px] truncate leading-none">${title}</span>${rangeHtml}<button data-reference-remove type="button" class="${CHIP_REMOVE_BUTTON_CLASS}" onmousedown="event.preventDefault()"><svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg></button></span>`
}

/**
 * Line range of a rendered chip. Reads data-line-start/end (the visible label is ":29" / ":12-18");
 * falls back to the legacy "(12-18)" text so older chips still resolve.
 * Regression: the sync effect used to parse only "(12-18)" and deleted every ranged chip.
 */
export function readChipLineRange(el: Element): { lineStart?: number; lineEnd?: number } {
  const num = (v: string | null) => (v !== null && v !== '' && !Number.isNaN(Number(v)) ? Number(v) : undefined)
  const attrStart = num(el.getAttribute('data-line-start'))
  const attrEnd = num(el.getAttribute('data-line-end'))
  if (attrStart !== undefined) return { lineStart: attrStart, lineEnd: attrEnd ?? attrStart }
  const m = (el.textContent || '').match(/\((\d+)(?:-(\d+))?\)/)
  if (!m) return {}
  const lineStart = parseInt(m[1]!, 10)
  return { lineStart, lineEnd: m[2] ? parseInt(m[2], 10) : lineStart }
}

/** Same document + line identity as insertReferenceAtCursor / mention duplicate checks. */
export function referenceListedWithSameKey(
  refs: DocumentReference[],
  ref: DocumentReference
): boolean {
  return refs.some((r) => {
    if (r.id !== ref.id) return false
    if (r.lineStart !== undefined && ref.lineStart !== undefined) {
      return r.lineStart === ref.lineStart && r.lineEnd === ref.lineEnd
    }
    if (r.lineStart === undefined && ref.lineStart === undefined) return true
    return false
  })
}

/**
 * True if an inline document reference chip is already in the editor for this ref.
 * Uses data-line-start/end when present so multiple line-ranged refs to one doc work.
 */
export function documentReferenceChipPresentInEditor(
  root: HTMLElement,
  ref: DocumentReference
): boolean {
  const idSel = CSS.escape(ref.id)
  const chips = root.querySelectorAll(`[data-reference-id="${idSel}"][data-reference-type="chip"]`)
  for (const el of chips) {
    if (!(el instanceof HTMLElement)) continue
    if (el.getAttribute('data-url-chip') === 'true') continue

    const ds = el.getAttribute('data-line-start')
    const de = el.getAttribute('data-line-end')
    const chipHasLines = ds != null
    const refHasLines = ref.lineStart !== undefined

    if (!refHasLines && !chipHasLines) return true

    if (refHasLines && chipHasLines) {
      const cs = parseInt(ds, 10)
      const ce = de != null ? parseInt(de, 10) : cs
      const rs = ref.lineStart!
      const re = ref.lineEnd ?? ref.lineStart!
      if (cs === rs && ce === re) return true
    }
  }
  return false
}

export function extractPlainText(element: HTMLElement): string {
  // Recursively walk nodes, skipping chips, and preserving newlines from <br> and block elements
  const BLOCK_TAGS = new Set(['DIV', 'P', 'BR', 'LI', 'BLOCKQUOTE', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6'])

  function walk(node: Node, isFirst: boolean): string {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent || ''
    }
    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as Element
      // URL chips: return the full URL so it is included in the sent message
      if (el.getAttribute('data-url-chip') === 'true') {
        return el.getAttribute('data-url') || el.textContent || ''
      }
      // Code block chips: return the original ``` fenced block
      if (el.getAttribute('data-code-block') === 'true') {
        const lang = el.getAttribute('data-language') || ''
        const code = el.getAttribute('data-code') || el.querySelector('code')?.textContent || ''
        return `\`\`\`${lang}\n${code}\n\`\`\``
      }
      // Skip other chips entirely
      if (el.getAttribute('data-reference-type') === 'chip') {
        return ''
      }
      const tag = el.tagName.toUpperCase()
      // <br> becomes a newline
      if (tag === 'BR') {
        return '\n'
      }
      // Block elements: collect children then add a trailing newline (except for the root element itself)
      let inner = ''
      let childIndex = 0
      for (const child of Array.from(el.childNodes)) {
        inner += walk(child, childIndex === 0)
        childIndex++
      }
      if (BLOCK_TAGS.has(tag) && !isFirst) {
        // Add leading newline for block-level elements after the first
        return '\n' + inner
      }
      return inner
    }
    return ''
  }

  let text = walk(element, true)

  // Remove reference markers if any
  text = text.replace(/\[REF:[^\]]+\]/g, '')
  // Remove leading/trailing newlines only (preserve internal ones)
  return text.replace(/^\n+|\n+$/g, '')
}

const UUID_DOC_RE_INLINE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** Serialize DOM back to plain text with embedded FN_* markers so folder/doc/skill chips survive send/history. */
function serializeReferenceChip(el: Element): string {
  const skillId = el.getAttribute('data-skill-id')
  if (skillId) {
    const name =
      (el.getAttribute('data-skill-name') || '').trim() ||
      (el.querySelector('span.truncate')?.textContent || '').trim() ||
      skillId
    return `{{FN_SKILL:${encodeURIComponent(skillId)}|${encodeURIComponent(name)}}}`
  }

  const kindAttr = el.getAttribute('data-reference-kind')
  const pathAttr = el.getAttribute('data-reference-path') || ''
  const id = el.getAttribute('data-reference-id') || ''
  const titleRaw = el.getAttribute('data-reference-title') || ''

  const serializeFolder = (folderPath: string) => {
    if (!folderPath) return ''
    const title =
      titleRaw.trim() ||
      folderPath.split('/').filter(Boolean).pop() ||
      folderPath
    return `{{FN_FOLDER:${encodeURIComponent(folderPath)}|${encodeURIComponent(title)}}}`
  }

  const serializeDoc = (docId: string) => {
    if (!docId) return ''
    const title = titleRaw.trim() || docId
    const lineStart = el.getAttribute('data-line-start')
    const lineEnd = el.getAttribute('data-line-end')
    const excerpt = el.getAttribute('data-excerpt')
    const excerptTail = el.getAttribute('data-excerpt-tail')
    return formatFnDocMarker(docId, title, lineStart, lineEnd, excerpt, excerptTail)
  }

  if (kindAttr === 'folder') {
    return serializeFolder(pathAttr || id)
  }
  if (kindAttr === 'document') {
    return serializeDoc(id)
  }

  // Legacy chips without data-reference-kind
  if (UUID_DOC_RE_INLINE.test(id)) {
    return serializeDoc(id)
  }
  return serializeFolder(pathAttr || id)
}

/** First sentence, or this many characters, stored on a paste/hover chip for the agent. */
export const PASTE_REF_EXCERPT_MAX = 160
/** Closing words of a long selection, so a repeated opening can still be found. */
export const PASTE_REF_TAIL_LEN = 80

/**
 * Short quote of what the user actually selected. Line numbers can point at an
 * earlier block; this lets the agent check the chip against the stored body.
 */
export function clipPasteReferenceExcerpt(text: string | null | undefined): string {
  if (!text) return ''
  const flat = text.replace(/\s+/g, ' ').trim()
  if (!flat) return ''
  if (flat.endsWith('...') && flat.length <= PASTE_REF_EXCERPT_MAX + 3) return flat
  if (flat.length <= PASTE_REF_EXCERPT_MAX) return flat
  const sentence = flat.match(/^.{12,160}?[.!?]/)
  if (sentence) return sentence[0]
  return `${flat.slice(0, PASTE_REF_EXCERPT_MAX).trimEnd()}...`
}

/**
 * Last words of a selection that is longer than the opening quote.
 * Empty when the opening already covers the selection.
 */
export function clipPasteReferenceTail(text: string | null | undefined): string {
  if (!text) return ''
  const flat = text.replace(/\s+/g, ' ').trim()
  if (!flat) return ''
  const head = clipPasteReferenceExcerpt(text)
  if (!head || flat.length <= head.length + 24) return ''
  const tail = flat.slice(-PASTE_REF_TAIL_LEN).trim()
  if (!tail || head.includes(tail)) return ''
  return tail
}

function storedPasteTail(excerpt?: string | null, excerptTail?: string | null): string {
  const explicit = (excerptTail || '').replace(/\s+/g, ' ').trim()
  if (explicit) return explicit.slice(-PASTE_REF_TAIL_LEN)
  return clipPasteReferenceTail(excerpt)
}

/** Persist hover/paste line range: `{{FN_DOC:id|title|12-18|excerpt|tail}}`. */
export function formatFnDocMarker(
  docId: string,
  title: string,
  lineStart?: string | number | null,
  lineEnd?: string | number | null,
  excerpt?: string | null,
  excerptTail?: string | null
): string {
  const base = `{{FN_DOC:${encodeURIComponent(docId)}|${encodeURIComponent(title)}`
  const start = Number(lineStart)
  if (!Number.isFinite(start) || start < 1) return `${base}}}`
  const end = Number(lineEnd)
  const range =
    Number.isFinite(end) && end !== start ? `${start}-${end}` : `${start}`
  const clipped = clipPasteReferenceExcerpt(excerpt)
  const tail = clipped ? storedPasteTail(excerpt, excerptTail) : ''
  const quote = clipped ? `|${encodeURIComponent(clipped)}` : ''
  const tailPart = tail ? `|${encodeURIComponent(tail)}` : ''
  return `${base}|${range}${quote}${tailPart}}}`
}

/** Build inline FN_* markers for references (ingestion send fallback when DOM is unavailable). */
export function referencesToInlineMarkers(
  refs: Array<{
    id: string
    title?: string
    path?: string
    type?: string
    kind?: string
    lineStart?: number
    lineEnd?: number
    excerpt?: string
    excerptTail?: string
  }>
): string {
  return refs
    .map((ref) => {
      if (ref.kind === 'folder' || ref.type === 'folder') {
        const folderPath = ref.path || ref.id
        if (!folderPath) return ''
        const title =
          ref.title?.trim() ||
          folderPath.split('/').filter(Boolean).pop() ||
          folderPath
        return `{{FN_FOLDER:${encodeURIComponent(folderPath)}|${encodeURIComponent(title)}}}`
      }
      const docId = ref.id
      if (!docId) return ''
      const title = ref.title?.trim() || docId
      return formatFnDocMarker(docId, title, ref.lineStart, ref.lineEnd, ref.excerpt, ref.excerptTail)
    })
    .filter(Boolean)
    .join(' ')
}

export function serializeMessagePlainText(element: HTMLElement): string {
  const BLOCK_TAGS = new Set(['DIV', 'P', 'BR', 'LI', 'BLOCKQUOTE', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6'])

  function walk(node: Node, isFirst: boolean): string {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent || ''
    }
    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as Element
      if (el.getAttribute('data-url-chip') === 'true') {
        return el.getAttribute('data-url') || el.textContent || ''
      }
      if (el.getAttribute('data-code-block') === 'true') {
        const lang = el.getAttribute('data-language') || ''
        const code = el.getAttribute('data-code') || el.querySelector('code')?.textContent || ''
        return `\`\`\`${lang}\n${code}\n\`\`\``
      }
      if (el.getAttribute('data-reference-type') === 'chip') {
        return serializeReferenceChip(el)
      }
      const tag = el.tagName.toUpperCase()
      if (tag === 'BR') {
        return '\n'
      }
      let inner = ''
      let childIndex = 0
      for (const child of Array.from(el.childNodes)) {
        inner += walk(child, childIndex === 0)
        childIndex++
      }
      if (BLOCK_TAGS.has(tag) && !isFirst) {
        return '\n' + inner
      }
      return inner
    }
    return ''
  }

  let text = walk(element, true)
  text = text.replace(/\[REF:[^\]]+\]/g, '')
  return text.replace(/^\n+|\n+$/g, '')
}

function canUseTreeWalkerRoot(root: Node | null): root is Node {
  if (!root) return false
  if (root instanceof HTMLElement) return root.isConnected
  const parent = root.parentElement ?? (root.parentNode instanceof HTMLElement ? root.parentNode : null)
  return parent?.isConnected ?? false
}

export function getCaretPosition(element: HTMLElement, range: Range): number {
  if (!element.isConnected) return 0
  const preCaretRange = range.cloneRange()
  preCaretRange.selectNodeContents(element)
  preCaretRange.setEnd(range.endContainer, range.endOffset)
  const walkerRoot = preCaretRange.commonAncestorContainer
  if (!canUseTreeWalkerRoot(walkerRoot)) return 0
  // Count only text nodes, not chips
  let textLength = 0
  const walker = document.createTreeWalker(
    walkerRoot,
    NodeFilter.SHOW_TEXT,
    null
  )
  let node
  while (node = walker.nextNode()) {
    if (preCaretRange.comparePoint(node, 0) <= 0) {
      textLength += node.textContent?.length || 0
    }
  }
  return textLength
}

export function restoreCaretPosition(element: HTMLElement, offset: number) {
  if (!(element instanceof HTMLElement) || !element.isConnected) return
  const selection = window.getSelection()
  if (!selection) return

  const range = document.createRange()
  let currentOffset = 0
  let walker: TreeWalker
  try {
    walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, null)
  } catch {
    return
  }
  let node: Node | null = null

  while (node = walker.nextNode()) {
    const nodeLength = node.textContent?.length || 0
    if (currentOffset + nodeLength >= offset) {
      range.setStart(node, offset - currentOffset)
      range.setEnd(node, offset - currentOffset)
      selection.removeAllRanges()
      selection.addRange(range)
      return
    }
    currentOffset += nodeLength
  }

  // If we didn't find a position, set to end
  if (node) {
    range.setStartAfter(node)
    range.setEndAfter(node)
    selection.removeAllRanges()
    selection.addRange(range)
  }
}

export function renderUrlChipAsHTML(url: string): string {
  let display = url
  try {
    const u = new URL(url)
    display = u.hostname + (u.pathname !== '/' ? u.pathname : '')
  } catch { /* use raw url */ }
  const safeUrl = escapeHtml(url)
  const safeDisplay = escapeHtml(display)
  // Link icon SVG inline, muted primary color chip
  const linkSvg = `<svg class="w-3 h-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/></svg>`
  return `<span data-url-chip="true" data-url="${safeUrl}" data-reference-type="chip" contenteditable="false" title="${safeUrl}" class="${INLINE_CHIP_BASE_CLASS} cursor-pointer bg-muted/60 text-muted-foreground border-border hover:bg-muted hover:text-foreground">${linkSvg}<span class="truncate max-w-[220px] leading-none">${safeDisplay}</span></span>`
}

export function renderCodeBlockAsHTML(lang: string, code: string): string {
  const safeCode = escapeHtml(code)
  const safeLang = escapeHtml(lang)
  const langBadge = lang
    ? `<span class="text-[10px] font-mono text-muted-foreground px-1">${safeLang}</span>`
    : ''
  const removeSvg = `<svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>`
  return `<div data-code-block="true" data-language="${safeLang}" data-code="${escapeHtml(code)}" data-reference-type="chip" contenteditable="false" class="my-1.5 rounded-md border border-border bg-muted/40 overflow-hidden text-left w-full max-w-full select-none"><div class="flex items-center justify-between px-2 py-1 bg-muted/60 border-b border-border"><span class="text-[10px] text-muted-foreground font-medium">code${langBadge ? ' · ' + lang : ''}</span><button data-code-block-remove type="button" class="p-0.5 hover:bg-black/10 dark:hover:bg-white/10 rounded text-muted-foreground flex-shrink-0 transition-colors" onmousedown="event.preventDefault()">${removeSvg}</button></div><pre class="px-3 py-2 text-[12px] font-mono leading-relaxed overflow-x-auto m-0 bg-transparent whitespace-pre text-foreground/90"><code>${safeCode}</code></pre></div>`
}
